import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { History as HistoryIcon } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import { getUnitAccessLogs, flagVisit, clearVisitFlag } from '@/services/api.client';
import { invitationTypeLabel } from '@/constants/labels';
import type { AccessLogEntry } from '@/types';
import { formatDateTime, statusBadgeClass } from '@/lib/utils';

function formatDuration(minutes: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60 > 0 ? `${minutes % 60}m` : ''}`.trim();
}

export function PassHistoryPage() {
  const { isLoading: isAuthLoading } = useAuth();
  const prop = useResidentProperty();
  const [search, setSearch] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['access-logs', prop?.propertyId, prop?.unitId],
    queryFn: () => (prop ? getUnitAccessLogs(prop.propertyId, prop.unitId) : null),
    enabled: !!prop,
  });

  const queryClient = useQueryClient();
  const invalidate = () =>
    void queryClient.invalidateQueries({
      queryKey: ['access-logs', prop?.propertyId, prop?.unitId],
    });

  const flagMutation = useMutation({
    mutationFn: ({ visitId, reason }: { visitId: string; reason: string }) => {
      if (!prop) return Promise.reject(new Error('No property'));
      return flagVisit(prop.propertyId, visitId, reason);
    },
    onSuccess: invalidate,
  });

  const clearMutation = useMutation({
    mutationFn: (visitId: string) => {
      if (!prop) return Promise.reject(new Error('No property'));
      return clearVisitFlag(prop.propertyId, visitId);
    },
    onSuccess: invalidate,
  });

  const isFlagBusy = flagMutation.isPending || clearMutation.isPending;

  function handleFlag(visitId: string) {
    const reason = window.prompt('Why should this visitor be held at the gate?');
    if (reason && reason.trim().length >= 3) {
      flagMutation.mutate({ visitId, reason: reason.trim() });
    }
  }

  const logs = (data?.data ?? []).filter((l) =>
    search ? l.visitorName.toLowerCase().includes(search.toLowerCase()) : true,
  );

  function handleExportCsv() {
    if (!data?.data.length) return;
    const headers = ['Visitor', 'Type', 'Date', 'Gate', 'Status', 'Duration', 'Guard'];
    const rows = data.data.map((l: AccessLogEntry) => [
      l.visitorName,
      l.invitationType,
      formatDateTime(l.checkedInAt),
      l.gateName,
      l.status,
      formatDuration(l.durationMinutes),
      l.guardName ?? '—',
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'visitor-history.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl">Visitor History</h1>

      {/* Filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Search visitor name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={handleExportCsv}>
          Export CSV
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[var(--bg-surface-raised)] overflow-x-auto">
        {isError ? (
          <div
            role="alert"
            className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
          >
            <p className="text-sm font-medium">Couldn&apos;t load pass history</p>
            <p className="text-sm text-muted-foreground">Please try again later.</p>
          </div>
        ) : isLoading || isAuthLoading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center">
            <HistoryIcon className="size-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm font-medium text-muted-foreground">No visitor history yet.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Past visits will appear here once your visitors check in at the gate.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bg-surface-raised)] bg-[var(--bg-surface-raised)]">
                {['Visitor', 'Type', 'Date', 'Gate', 'Status', 'Duration', 'Guard', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr
                  key={log.id}
                  className="border-b border-[var(--bg-surface-raised)] last:border-0 hover:bg-[var(--bg-surface-raised)] transition-colors"
                >
                  <td className="px-4 py-3 text-sm font-medium text-[var(--text-primary)]">
                    {log.visitorName}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusBadgeClass(log.invitationType)}>
                      {invitationTypeLabel(log.invitationType)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                    {formatDateTime(log.checkedInAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">{log.gateName}</td>
                  <td className="px-4 py-3">
                    <Badge className={statusBadgeClass(log.status)}>
                      {log.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                    {formatDuration(log.durationMinutes)}
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                    {log.guardName ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {log.status === 'granted' &&
                      (log.isFlagged ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isFlagBusy}
                          onClick={() => clearMutation.mutate(log.id)}
                        >
                          Remove flag
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isFlagBusy}
                          onClick={() => handleFlag(log.id)}
                        >
                          Flag
                        </Button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
